import React, { useState, useMemo } from 'react';
import LoadingState from './TransactionHistory/LoadingState';
import EmptyState from './TransactionHistory/EmptyState';
import TransactionRow from './TransactionHistory/TransactionRow';
import Pagination from './TransactionHistory/Pagination';
import { Search } from 'lucide-react';
import {
    formatTransactionType,
    formatTransactionAmount,
    formatTransactionAddress,
    formatTransactionDate,
    getTransactionHash,
    getStatusClass
} from '../../utils/transactionUtils';


const TransactionHistory = ({
    transactions,
    loading,
    pagination,
    onPageChange,
    onTransactionClick
}) => {
    const [searchTerm, setSearchTerm] = useState('');

    const filteredTransactions = useMemo(() => {
        if (!searchTerm.trim()) return transactions;

        const term = searchTerm.toLowerCase().trim();
        return transactions.filter(tx => {
            const type = formatTransactionType(tx).toLowerCase();
            const amount = formatTransactionAmount(tx).toLowerCase();
            const address = formatTransactionAddress(tx ?? '').toLowerCase();
            const date = formatTransactionDate(tx).toLowerCase();
            const hash = (getTransactionHash(tx) || '').toLowerCase();
            const status = (tx.status || 'unknown').toLowerCase();
            const functionName = (tx.functionName || '').toLowerCase();

            return type.includes(term) ||
                amount.includes(term) ||
                address.includes(term) ||
                date.includes(term) ||
                hash.includes(term) ||
                status.includes(term) ||
                functionName.includes(term);
        });
    }, [transactions, searchTerm]);

    return (
        <div className="dashboard-grid">
            <div className="lg:flex justify-between gap-2 mb-4 items-center table-filter">
                <h4 className='font-semibold md:m-0 mb-3 text-18'>Transaction history</h4>
                {/* <div className="relative">
                    <input
                        type="text"
                        className="form-control m-0 lg:w-auto"
                        style={{ minWidth: '300px' }}
                        placeholder="Search by keyword..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <span className="absolute left-4" style={{ top: '50%', transform: 'translateY(-50%)' }}>
                        <Search size={18} className="text-gray-500" />
                    </span>
                </div> */}
            </div>


            <div className="card-body p-0">
                {/* Mobile View */}
                <div className="lg:hidden space-y-3">
                    {loading ? <LoadingState /> :
                        !Array.isArray(filteredTransactions) || filteredTransactions.length === 0 ? <EmptyState /> : (
                            filteredTransactions.map((tx, index) => {
                                const type = formatTransactionType(tx);
                                const hash = getTransactionHash(tx);
                                return (
                                    <div
                                        key={`tx-mobile-${index}`}
                                        className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700"
                                        onClick={() => onTransactionClick && onTransactionClick(tx)}
                                    >
                                        <div className="flex justify-between items-start mb-2">
                                            <span className={`font-bold ${type === "Send" || type === "Unshield" || type === "Unshielded" ? "text-danger" : "text-success"}`}>
                                                {type}
                                            </span>
                                            <span className={getStatusClass(tx.status)}>
                                                {tx.status || 'Unknown'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-baseline mb-2">
                                            <div className="font-semibold text-lg">
                                                {formatTransactionAmount(tx)}
                                            </div>
                                            <div className="text-muted text-12">
                                                {formatTransactionDate(tx)}
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            <div className="flex justify-between text-12">
                                                <span className="text-gray-500">Address:</span>
                                                <span className="font-mono">{formatTransactionAddress(tx)}</span>
                                            </div>
                                            <div className="flex justify-between text-12">
                                                <span className="text-gray-500">Hash:</span>
                                                <span className="font-mono">{hash ? `${hash.slice(0, 10)}...` : 'N/A'}</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )
                    }
                </div>

                {/* Desktop View */}
                <div className="table-responsive hidden lg:block">
                    {loading ? <LoadingState /> :
                        !Array.isArray(filteredTransactions) || filteredTransactions.length === 0 ? <EmptyState /> : (
                            <table className="table align-middle mb-0">
                                <thead className="">
                                    <tr>
                                        <th className="">Type</th>
                                        <th className="">Amount</th>
                                        <th className="">Address</th>
                                        <th className="" style={{ width: 200 }}>Date</th>
                                        <th className="">Hash</th>
                                        <th className="">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredTransactions.map((tx, index) => (
                                        <TransactionRow
                                            key={index}
                                            tx={tx}
                                            index={index}
                                            onTransactionClick={onTransactionClick}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        )}
                </div>
            </div>

            <Pagination
                pagination={pagination}
                loading={loading}
                onPageChange={onPageChange}
            />
        </div>
    );
};

export default TransactionHistory;
